/**
 * Search Result Container.
 * It helps to display the Search Result using Data List Component.
 * The `DataList` Component internally read the data and pass with pagination as per Config Props.
 */

import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { Button, Row, Col } from 'kepler';

// Components and Shapes.
import Notifier from '../../contactUs/containers/Notifier';
import { DataList } from '../../contactUs/components/common/index';
import MobileFilterModal from './MobileFilterModal';

// Actions.
import { fetchArticleSearchResult } from '../../contactUs/actions/action-article-search';
import { mobileMenuOpen } from '../../contactUs/actions/action-mobile-filter';

// Helpers.
import Page from '../../lib/page';
import i18n from '../../lib/reacti18n';
import { trackLink } from '../../lib/analytics-helper';
import contentHelper from '../../lib/contentHelper';

// Locales.
const searchResult = {
  title: i18n.message('smarthelp/searchResult.searchResult.title'),
  empty: i18n.message('smarthelp/searchResult.searchResult.empty'),
  subTitle: i18n.message('smarthelp/searchResult.searchResult.subTitle'),
  errorOccured: i18n.message('smarthelp/searchResult.searchResult.errorOccured'),
  tryAgain: i18n.message('smarthelp/searchResult.searchResult.tryAgain'),
  helpCenterLink: i18n.link('smarthelp/searchResult.searchResult.link.helpCenter'),
};

const mobileFilter = {
  update: i18n.message('smarthelp/searchResult.searchResult.mobile.filter.update'),
};

const prev = i18n.message('smarthelp/pagination.prev');
const next = i18n.message('smarthelp/pagination.next');

const country = Page.getCountry();
const isMobileLayout = Page.isMobileLayout();
// const exp = Page.getUserExperience();

class SearchResult extends Component {

  // This Method focus to the first search result item, if its exist.
  static focusOnFirstItem() {
    const firstItem = document.querySelector('div.search-result-list.data-list ul.list-items li:first-child a');
    if (firstItem) {
      firstItem.focus();
    }
  }

  // This Static method return `Data List Component` Locales as one Props.
  static getLabels() {
    return {
      prev,
      next,
    };
  }

  // List of Pagination Click Event Methods.
  static getClickMethods() {
    return {
      previous(page) {
        trackLink({ id: 'searchPage', link: `smarthelp-search-result-previous-page|${page}` });
        SearchResult.focusOnFirstItem();
      },
      pageNumber(page) {
        trackLink({ id: 'searchPage', link: `smarthelp-search-result-page|${page}` });
        SearchResult.focusOnFirstItem();
      },
      next(page) {
        trackLink({ id: 'searchPage', link: `smarthelp-search-result-next-page|${page}` });
        SearchResult.focusOnFirstItem();
      },
    };
  }

  static getPanelClass() {
    let classPanel = 'search-result-panel';
    if (isMobileLayout) {
      classPanel += ' mobile-panel';
    }
    return classPanel;
  }

  constructor(props) {
    super(props);

    this.state = {
      q: props.q,
    };
    this.clickHandlerFilterBtn = this.clickHandlerFilterBtn.bind(this);
  }

  // This Method return Search Term and truncate, if its lengthier than 20 characters.
  getSearchTerm() {
    let searchTerm = this.props.q;

    if (searchTerm && searchTerm.length > 20) {
      searchTerm = `${this.props.q.substr(0, 20)}...`;
    }

    return searchTerm;
  }

  // This Method return the Result Notes as per Record Count.
  // Shows Record Count with Search Term, if there is records.
  // Shows No Record, if there is no records.
  getResultNotes() {
    const searchTerm = this.getSearchTerm();

    let notes = (
      <span className="result-stats empty">
        {contentHelper(searchResult.empty, { searchTerm })}
      </span>
    );
    if (Array.isArray(this.props.items) && this.props.items.length > 0) {
      notes = (
        <span className="result-stats">
          {contentHelper(searchResult.subTitle, { resultCount: this.props.items.length, searchTerm })}
        </span>
      );
    } else if (this.props.error) {
      notes = (
        <span className="result-stats error">
          <span className="title">{searchResult.errorOccured}</span>
          <div className="error-body">
            {searchResult.tryAgain}
            <a href="#go-to-help-center" className="error-redirect" onClick={e => this.goToHelpCenter(e)} aria-label={searchResult.helpCenterLink}>
              {searchResult.helpCenterLink}
            </a>
          </div>
        </span>
      );
    }

    return notes;
  }

  // This Method helps to read the `Search Result Response` and pass it as Data List Format.
  getListItems() {
    return this.props.items.map((item) => {
      const title = item.articleTitle;

      /**
       * Using Smarthelp FAQ page for search result
       * let link = item.url;
       * // Check if `item.url` is undefined and it will be added only for `BRC`, `DEVELOPER`, `PRODUCT`.
       * if (!link) {
       * link = `/${country}/smarthelp/${exp}/article/${item.articleId.toLowerCase()}`;
       *}
       */

      // TODO: Revert the change and search article url direct to the HCR
      const rank = item.rank;
      const articleTitle = Page.getFaqUrlFromTitle(title);
      const articleId = item.articleId;
      let link = item.url;

      // Check if `item.url` is undefined and it will be added only for `BRC`, `DEVELOPER`, `PRODUCT`.
      if (!item.url) {
        link = `/${country}/selfhelp/article/${articleTitle}-${articleId.toLowerCase()}/${rank}`;
      }

      return {
        title,
        link,
        description: item.excerpt,
      };
    });
  }

  // This Method track the Help Center Link Click and redirect to Help Center Home.
  goToHelpCenter(e) {
    e.preventDefault();
    const link = `smarthelp-search-result-error-redirect-selfhelp|${this.props.q}`;
    trackLink({ id: 'searchPage', link });
    const cobrand = Page.getCountry();
    const redirectLink = `/${cobrand}/selfhelp/home`;
    Page.goTo(redirectLink);
  }

  clickHandlerFilterBtn() {
    this.props.mobileMenuOpen();
  }

  renderFilterButton() {
    if (!isMobileLayout) {
      return null;
    }

    return (
      <Button className="filter-btn" onClick={this.clickHandlerFilterBtn}>
        { mobileFilter.update }
      </Button>
    );
  }

  renderSearchPanel() {
    let rowClass = SearchResult.getPanelClass();
    // The Error page without filter button
    if (this.props.error) {
      rowClass = `${SearchResult.getPanelClass()} error`;
    }

    // The Panel Grid System which only be used at xs and sm
    return (
      <Row className={rowClass}>
        <Col xs="3/5" sm="2/3" className="result-notes-container">
          {this.getResultNotes()}
        </Col>
        <Col xs="2/5" sm="1/3" className="filter-btn-container">
          {this.renderFilterButton()}
        </Col>
        <MobileFilterModal />
      </Row>
    );
  }

  // Render the `SearchResult` Container.
  render() {
    return (
      <div id="search-result">
        <Notifier />
        <h1>{searchResult.title}</h1>
        {this.renderSearchPanel()}
        <DataList
          parentClass="search-result-list"
          items={this.getListItems()}
          pageSize={10}
          labels={SearchResult.getLabels()}
          onClick={SearchResult.getClickMethods()}
          paginate
          showGoToPage={!isMobileLayout}
        />
      </div>
    );
  }
}

// SearchResult Props Types.
SearchResult.propTypes = {
  // Method Prop Types.
  q: PropTypes.string.isRequired,
  error: PropTypes.bool.isRequired,
  items: PropTypes.arrayOf(PropTypes.shape({
    title: PropTypes.string,
    link: PropTypes.string,
    description: PropTypes.string,
  })),
  mobileMenuOpen: PropTypes.func.isRequired,
};

// SearchResult Props default values.
SearchResult.defaultProps = {
  q: '',
  error: false,
  items: [],
};

// Dispatch the Action as Props.
function mapDispatchToProps(dispatch) {
  return bindActionCreators({ fetchArticleSearchResult, mobileMenuOpen }, dispatch);
}

// Get the Store data and Pass it as Props.
function mapStateToProps(state) {
  return {
    q: state.searchResult.q,
    error: state.searchResult.error,
    items: state.searchResult.items,
  };
}

export default connect(mapStateToProps, mapDispatchToProps)(SearchResult);



// WEBPACK FOOTER //
// ./home/containers/SearchResult.js